/**
 * 检验任务列表页（v0.84 / P3）。
 *
 * 定位：`工序 SIP`（人勾好的气泡 + 公差）→ **本页建任务** → P4 执行界面逐项测量。
 *
 * 三条与后端对齐的口径
 * -------------------
 * 1. **明细不在建单请求里。** 建任务只需要说「打在哪张图 + 依哪份 SIP」，
 *    明细由后端从 SIP **快照**过来。界面上那句提示要写清楚，
 *    否则用户会以为"怎么没有选检验项的地方"。
 * 2. **取不到明细会 400。** 工序 SIP 还没勾气泡时，后端拒绝建单并给出明确原因，
 *    这里原样显示 —— 不吞成"创建失败"。
 * 3. **已完成的不能撤销。** 后端 409；界面在按钮上也提前禁用，
 *    别让用户点了才知道不行（那是受控记录，不是普通数据）。
 */
import { useCallback, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cancelInspectionTask, createInspectionTask, fetchInspectionTask,
  fetchInspectionTasks, findTaskCandidates,
  type InspectionTaskRow,
} from '../api/client';
// v0.85（P4）：执行界面 —— 双模式逐格录入 + 并排气泡定位。
import { InspectionRunner } from './InspectionRunner';

type Toast = (m: string, t?: 'info' | 'success' | 'error') => void;

const TASK_TYPES: Array<[string, string]> = [
  ['incoming', '进料检验'],
  ['first', '首检'],
  ['last', '末检'],
  ['patrol', '巡检'],
  ['full', '全尺寸'],
];

/** 状态 → 中文 + 配色（与后端 `TASK_STATUSES` 同源）。 */
const STATUS_LABEL: Record<string, [string, string]> = {
  pending: ['待执行', '#8a6d3b'],
  in_progress: ['执行中', '#1f6feb'],
  done: ['已完成', '#1a7f37'],
  closed: ['已关闭', '#57606a'],
  abandoned: ['已撤销', '#9a3412'],
};

function typeLabel(t: string): string {
  return TASK_TYPES.find(([k]) => k === t)?.[1] ?? t;
}

function fmtTime(s: string | null): string {
  if (!s) return '—';
  // 后端返回带时区的 ISO；这里只截到分钟，避免一长串毫秒挤爆列宽
  return s.slice(0, 16).replace('T', ' ');
}

export function InspectionTasksPage({ canEdit, onToast }: {
  canEdit: boolean;
  onToast: Toast;
}) {
  const qc = useQueryClient();
  const [status, setStatus] = useState('');
  const [source, setSource] = useState('');
  const [q, setQ] = useState('');
  const [detail, setDetail] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  /** 正在执行的任务单号（null = 停在列表）。执行时**整页替换**成执行界面 —— */
  const [running, setRunning] = useState<string | null>(null);

  const listQ = useQuery({
    queryKey: ['inspection-tasks', status, source, q],
    queryFn: () => fetchInspectionTasks({
      status: status || undefined,
      source: source || undefined,
      q: q || undefined,
      limit: 300,
    }),
    refetchOnWindowFocus: false,
  });
  const tasks = useMemo<InspectionTaskRow[]>(
    () => listQ.data?.tasks ?? [], [listQ.data]);

  const refresh = useCallback(
    () => qc.invalidateQueries({ queryKey: ['inspection-tasks'] }), [qc]);

  const doCancel = useCallback(async (t: InspectionTaskRow) => {
    if (!window.confirm(
      `撤销任务「${t.task_no}」？\n\n撤销后不能恢复（要作废请另建一张）。`)) return;
    try {
      const r = await cancelInspectionTask(t.task_no, '界面撤销');
      onToast(r.changed ? `已撤销 ${t.task_no}` : '这张任务本来就是撤销状态',
              r.changed ? 'success' : 'info');
      await refresh();
    } catch (e: any) {
      onToast(`撤销失败：${e.message}`, 'error');
    }
  }, [onToast, refresh]);

  // 执行态整页替换（与 SIP 页同一取舍：不把两套编辑区并排挤在一个页面里）
  if (running) {
    return (
      <InspectionRunner
        taskNo={running}
        canEdit={canEdit}
        onToast={onToast}
        onBack={() => { setRunning(null); void refresh(); }}
      />
    );
  }

  return (
    <div className="page-pad" style={{ padding: 16, overflow: 'auto', height: '100%' }}>
      {/* ---------- 工具栏 ---------- */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center',
                    flexWrap: 'wrap', marginBottom: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18 }}>检验任务</h2>
        <span style={{ fontSize: 12, opacity: 0.65 }}>
          明细来自 SIP 快照（建单后不再随图纸变动）
        </span>
        <span style={{ flex: 1 }} />
        <select className="btn sm" value={status}
                onChange={(e) => setStatus(e.target.value)}>
          <option value="">全部状态</option>
          {Object.entries(STATUS_LABEL).map(([k, [label]]) => (
            <option key={k} value={k}>{label}</option>
          ))}
        </select>
        <select className="btn sm" value={source}
                onChange={(e) => setSource(e.target.value)}>
          <option value="">全部来源</option>
          <option value="manual">本平台</option>
          <option value="asn">ASN</option>
          <option value="mes">MES</option>
        </select>
        <input className="btn sm" style={{ width: 200 }}
               placeholder="单号 / 物料 / 批次 / 外部单号"
               value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn sm" onClick={() => refresh()}>刷新</button>
        {canEdit && (
          <button className="btn sm primary" onClick={() => setCreating(true)}>
            + 新建任务
          </button>
        )}
      </div>

      {/* ---------- 列表 ---------- */}
      {listQ.isLoading && <div className="sip-state">正在读取任务…</div>}
      {listQ.isError && (
        <div className="sip-state">
          <div className="sip-state-title">任务列表打不开</div>
          <div className="sip-state-desc">{(listQ.error as any)?.message}</div>
        </div>
      )}
      {!listQ.isLoading && !listQ.isError && tasks.length === 0 && (
        <div className="sip-state">
          <div className="sip-state-icon">◇</div>
          <div className="sip-state-title">还没有检验任务</div>
          <div className="sip-state-desc">
            点右上角「新建任务」：选一张已归户的图纸 → 选一道已填 SIP 的工序 → 建单。
          </div>
        </div>
      )}

      {tasks.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>
              <th style={{ padding: '6px 8px' }}>单号</th>
              <th style={{ padding: '6px 8px' }}>类型</th>
              <th style={{ padding: '6px 8px' }}>来源</th>
              <th style={{ padding: '6px 8px' }}>物料 / 零件号</th>
              <th style={{ padding: '6px 8px' }}>批次</th>
              <th style={{ padding: '6px 8px' }}>进度</th>
              <th style={{ padding: '6px 8px' }}>状态</th>
              <th style={{ padding: '6px 8px' }}>创建</th>
              <th style={{ padding: '6px 8px' }} />
            </tr>
          </thead>
          <tbody>
            {tasks.map((t) => {
              const [label, color] = STATUS_LABEL[t.status] ?? [t.status, '#666'];
              const canCancel = canEdit
                && t.status !== 'abandoned'
                && t.status !== 'done' && t.status !== 'closed';
              return (
                <tr key={t.task_no} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>
                    {t.task_no}
                  </td>
                  <td style={{ padding: '6px 8px' }}>{typeLabel(t.task_type)}</td>
                  <td style={{ padding: '6px 8px', opacity: 0.8 }}>{t.source}</td>
                  <td style={{ padding: '6px 8px' }}>
                    {t.material_code || '—'}
                    {t.part_no && t.part_no !== t.material_code
                      ? <span style={{ opacity: 0.6 }}> / {t.part_no}</span> : null}
                  </td>
                  <td style={{ padding: '6px 8px' }}>{t.lot_no || '—'}</td>
                  <td style={{ padding: '6px 8px', minWidth: 110 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ flex: 1, height: 6, background: '#eef2f7',
                                    borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${t.progress}%`, height: '100%',
                                      background: t.failed ? '#d1242f' : '#1a7f37' }} />
                      </div>
                      <span style={{ fontSize: 11, opacity: 0.75 }}>
                        {t.done}/{t.total}
                      </span>
                    </div>
                  </td>
                  <td style={{ padding: '6px 8px', color, fontWeight: 600 }}>
                    {label}
                  </td>
                  <td style={{ padding: '6px 8px', opacity: 0.7 }}>
                    {fmtTime(t.created_at)}
                  </td>
                  <td style={{ padding: '6px 8px', whiteSpace: 'nowrap' }}>
                    {canEdit
                      && (t.status === 'pending' || t.status === 'in_progress') && (
                      <button className="btn sm primary" style={{ marginRight: 6 }}
                              onClick={() => setRunning(t.task_no)}>
                        执行
                      </button>
                    )}
                    <button className="btn sm"
                            onClick={() => setDetail(t.task_no)}>查看</button>
                    {canCancel && (
                      <button className="btn sm" style={{ marginLeft: 6 }}
                              onClick={() => doCancel(t)}>撤销</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {detail && (
        <TaskDetailDialog taskNo={detail}
                          onClose={() => setDetail(null)} />
      )}
      {creating && canEdit && (
        <TaskCreateDialog
          onClose={() => setCreating(false)}
          onToast={onToast}
          onCreated={async (taskNo) => {
            setCreating(false);
            await refresh();
            setDetail(taskNo);
          }} />
      )}
    </div>
  );
}

/* ================================================================ 详情 */
function TaskDetailDialog({ taskNo, onClose }: {
  taskNo: string;
  onClose: () => void;
}) {
  const q = useQuery({
    queryKey: ['inspection-task', taskNo],
    queryFn: () => fetchInspectionTask(taskNo),
    refetchOnWindowFocus: false,
  });
  const t = q.data;
  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal tree-dialog" role="dialog" aria-modal="true"
           style={{ width: 'min(880px, 94vw)' }}>
        <div className="modal-title">
          {taskNo}
          {t && <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.7 }}>
            {typeLabel(t.task_type)} · {STATUS_LABEL[t.status]?.[0] ?? t.status}
            {' · '}{t.done}/{t.total} 项
          </span>}
        </div>
        {q.isLoading && <div className="sip-state">正在读取…</div>}
        {q.isError && (
          <div className="sip-state">
            <div className="sip-state-desc">{(q.error as any)?.message}</div>
          </div>
        )}
        {t && (
          <>
            <div style={{ fontSize: 12, opacity: 0.75, margin: '4px 0 10px' }}>
              物料 {t.material_code || '—'}
              {t.part_no ? ` ｜ 零件号 ${t.part_no}` : ''}
              {t.lot_no ? ` ｜ 批次 ${t.lot_no}` : ''}
              {t.qty != null ? ` ｜ 数量 ${t.qty}` : ''}
              {t.external_ref ? ` ｜ 外部单号 ${t.external_ref}` : ''}
            </div>
            <div style={{ maxHeight: '52vh', overflow: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ textAlign: 'left', borderBottom: '2px solid #e5e7eb' }}>
                    <th style={{ padding: 4 }}>#</th>
                    <th style={{ padding: 4 }}>气泡</th>
                    <th style={{ padding: 4 }}>检验项目</th>
                    <th style={{ padding: 4 }}>标准值</th>
                    <th style={{ padding: 4 }}>公差</th>
                    <th style={{ padding: 4 }}>量具</th>
                    <th style={{ padding: 4 }}>实测</th>
                    <th style={{ padding: 4 }}>判定</th>
                  </tr>
                </thead>
                <tbody>
                  {(t.items ?? []).map((i: any) => (
                    <tr key={i.seq} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ padding: 4 }}>{i.seq + 1}</td>
                      <td style={{ padding: 4 }}>{i.balloon_no ?? '—'}</td>
                      <td style={{ padding: 4 }}>{i.proj || '—'}</td>
                      <td style={{ padding: 4 }}>{i.nominal ?? '—'}</td>
                      <td style={{ padding: 4 }}>
                        {i.up != null || i.low != null
                          ? `${i.up ?? ''} / ${i.low ?? ''}` : '—'}
                      </td>
                      <td style={{ padding: 4 }}>{i.method || '—'}</td>
                      <td style={{ padding: 4 }}>{i.measured ?? '—'}</td>
                      <td style={{ padding: 4, color:
                          i.verdict === 'fail' ? '#d1242f'
                            : i.verdict === 'pass' ? '#1a7f37' : '#666' }}>
                        {i.verdict === 'pending' ? '未测' : i.verdict}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ fontSize: 12, opacity: 0.6, marginTop: 8 }}>
              以上为**建单时的快照**：此后图纸/SIP 再改，本表不会跟着变。
            </div>
          </>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
          <button className="btn" onClick={onClose}>关闭</button>
        </div>
      </div>
    </>
  );
}

/* ================================================================ 新建 */
function TaskCreateDialog({ onClose, onToast, onCreated }: {
  onClose: () => void;
  onToast: Toast;
  onCreated: (taskNo: string) => void;
}) {
  const [part, setPart] = useState('');
  const [picked, setPicked] = useState<{ drawingId: number; opId: number } | null>(null);
  const [taskType, setTaskType] = useState('first');
  const [lotNo, setLotNo] = useState('');
  const [qty, setQty] = useState('');
  const [busy, setBusy] = useState(false);

  const candQ = useQuery({
    queryKey: ['task-candidates', part.trim()],
    queryFn: () => findTaskCandidates(part.trim()),
    enabled: part.trim().length > 0,
    refetchOnWindowFocus: false,
  });
  const cands = candQ.data?.candidates ?? [];

  const submit = useCallback(async () => {
    if (!picked) { onToast('先选一道工序', 'error'); return; }
    setBusy(true);
    try {
      const r = await createInspectionTask({
        task_type: taskType,
        route_op_id: picked.opId,
        lot_no: lotNo || undefined,
        qty: qty ? Number(qty) : undefined,
      });
      onToast(r.duplicated
        ? `这张外部单已存在：${r.task_no}` : `已建任务 ${r.task_no}（${r.total} 项）`,
        'success');
      onCreated(r.task_no);
    } catch (e: any) {
      // 后端把「工序 SIP 还没勾气泡」这类原因写在 message 里，原样显示
      onToast(`建单失败：${e.message}`, 'error');
    } finally { setBusy(false); }
  }, [picked, taskType, lotNo, qty, onToast, onCreated]);

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal tree-dialog" role="dialog" aria-modal="true"
           style={{ width: 'min(720px, 94vw)' }}>
        <div className="modal-title">新建检验任务</div>
        <div className="sip-state-desc" style={{ margin: '4px 0 10px' }}>
          检验项由 <b>SIP 快照</b>过来 —— 这里只选「打在哪张图的哪道工序」，
          不需要（也不能）挑检验项。
        </div>

        <label style={{ display: 'block', marginBottom: 8 }}>
          <span style={{ fontSize: 12, opacity: 0.75 }}>物料编码 / 客户零件号</span>
          <input className="btn" style={{ width: '100%', textAlign: 'left' }}
                 placeholder="例如 PC0960023 或 030-B001-0537"
                 value={part} onChange={(e) => { setPart(e.target.value); setPicked(null); }} />
        </label>

        {candQ.isLoading && <div className="sip-state">正在查…</div>}
        {!candQ.isLoading && part.trim() && cands.length === 0 && (
          <div className="sip-state">
            <div className="sip-state-title">没有找到归户到该物料的图纸</div>
            <div className="sip-state-desc">
              SIP 的检验项来自图纸气泡 ⇒ 先上传图纸并归户到该物料。
            </div>
          </div>
        )}

        {cands.map((c) => (
          <div key={c.drawing_id} style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 600 }}>
              图纸 #{c.drawing_id} {c.filename}
            </div>
            {c.ops.length === 0 && (
              <div style={{ fontSize: 12, opacity: 0.65, padding: '2px 0 0 8px' }}>
                这张图纸还没有工序 —— 先到 SIP 页「导入工序」（工序只能来自 BOM）
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6,
                          padding: '4px 0 0 8px' }}>
              {c.ops.map((o) => {
                const on = picked?.opId === o.id;
                return (
                  <button key={o.id}
                          className={`btn sm${on ? ' primary' : ''}`}
                          onClick={() => setPicked({ drawingId: c.drawing_id, opId: o.id })}>
                    OP{o.op_no} {o.op_name}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
          <label>
            <span style={{ fontSize: 12, opacity: 0.75, display: 'block' }}>任务类型</span>
            <select className="btn" value={taskType}
                    onChange={(e) => setTaskType(e.target.value)}>
              {TASK_TYPES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label>
            <span style={{ fontSize: 12, opacity: 0.75, display: 'block' }}>批次号</span>
            <input className="btn" value={lotNo} placeholder="可选"
                   onChange={(e) => setLotNo(e.target.value)} />
          </label>
          <label>
            <span style={{ fontSize: 12, opacity: 0.75, display: 'block' }}>数量</span>
            <input className="btn" style={{ width: 90 }} value={qty} placeholder="可选"
                   onChange={(e) => setQty(e.target.value)} />
          </label>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
          <button className="btn" onClick={onClose}>取消</button>
          <button className="btn primary" disabled={!picked || busy}
                  onClick={submit}>
            {busy ? '建单中…' : '建任务'}
          </button>
        </div>
      </div>
    </>
  );
}
